import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import Button from './Button';
import Input from './Input';
import { productsAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';

const INITIAL = { name: '', unit: 'ton', description: '' };

const PeseurQuickProductModal = ({ isOpen, onClose, onProductCreated }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(INITIAL);
  }, [isOpen]);

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      toast.error(t('products.form.productNameRequired'));
      return;
    }
    setLoading(true);
    try {
      const res = await productsAPI.peseurQuickCreate({
        name: form.name.trim(),
        unit: form.unit || 'ton',
        description: form.description?.trim() || undefined
      });
      toast.success(t('weighing.quickProduct.success'));
      onProductCreated?.(res?.data?.product ?? null);
      handleClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || t('common.error')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('weighing.quickProduct.title')}
      size="md"
      closeOnOverlayClick={!loading}
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="peseur-quick-product-form" loading={loading}>
            {t('weighing.quickProduct.save')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-600 mb-4">{t('weighing.quickProduct.hint')}</p>
      <form id="peseur-quick-product-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t('products.productName')}
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            autoFocus
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('products.unit')} *
            </label>
            <select
              name="unit"
              value={form.unit}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="ton">{t('products.form.units.ton')}</option>
              <option value="kg">{t('products.form.units.kg')}</option>
              <option value="m³">{t('products.form.units.m3')}</option>
              <option value="liter">{t('products.form.units.liter')}</option>
              <option value="piece">{t('products.form.units.piece')}</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {`${t('products.description')} (${t('common.optional')})`}
          </label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </form>
    </Modal>
  );
};

export default PeseurQuickProductModal;
